import { useContext, useRef, useState } from "react";
import Modal from "../utils/modal"
import AuthContext from "../../context/authProvider";
import ToastContext from "../../context/toastProvider";
import { createSerial } from "./functions";
import Button from "../atoms/button";
import { generateSerial, getGradableModels } from "../gradableModels/functions";
import { getProduct } from "../products/functions";
import { gradingTypeSupportsGeneralScore, gradingTypeSupportsSubgrades } from "../utils/gradingTypes";

const CreateSerialModal = ({ setRegister, refresh }) => {

    const { user } = useContext(AuthContext);
    const { goodToast, badToast } = useContext(ToastContext);
    const [gradableModelId, setGradableModelId] = useState("");
    const [productId, setProductId] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [generalScore, setGeneralScore] = useState("");
    const [surface, setSurface] = useState("");
    const [edges, setEdges] = useState("");
    const [centering, setCentering] = useState("");
    const [corners, setCorners] = useState("");
    const [notaAutografo, setNotaAutografo] = useState("");
    const [supportsGeneralScore, setSupportsGeneralScore] = useState(true);
    const [supportsSubgrades, setSupportsSubgrades] = useState(true);
    const [supportsAutographNote, setSupportsAutographNote] = useState(false);
    const [supportResolutionLoading, setSupportResolutionLoading] = useState(false);
    const [resolvedProductId, setResolvedProductId] = useState("");
    const supportRequestIdRef = useRef(0);


    const handleClose = () => {
        setRegister(false);
    }

    const resolveSubgradesSupport = async (targetProductId) => {
        const currentRequestId = ++supportRequestIdRef.current;
        if (!targetProductId) {
            if (currentRequestId !== supportRequestIdRef.current) {
                return null;
            }
            setSupportsGeneralScore(true);
            setSupportsSubgrades(true);
            setSupportsAutographNote(false);
            setNotaAutografo("");
            setResolvedProductId("");
            setSupportResolutionLoading(false);
            return { supportsGeneralScore: true, supportsSubgrades: true, supportsAutographNote: false };
        }
        setSupportResolutionLoading(true);
        const productResponse = await getProduct(user?.token, targetProductId);
        if (currentRequestId !== supportRequestIdRef.current) {
            return null;
        }
        if (productResponse?.type === "success") {
            const supportsGeneral = gradingTypeSupportsGeneralScore(productResponse?.data?.gradingType);
            const supportsSub = gradingTypeSupportsSubgrades(productResponse?.data?.gradingType, productResponse?.data?.preLacreFormat);
            const supportsAutograph = productResponse?.data?.autographEvaluation === true;
            setSupportsGeneralScore(supportsGeneral);
            setSupportsSubgrades(supportsSub);
            setSupportsAutographNote(supportsAutograph);
            if (!supportsAutograph) {
                setNotaAutografo("");
            }
            setResolvedProductId(targetProductId);
            setSupportResolutionLoading(false);
            return { supportsGeneralScore: supportsGeneral, supportsSubgrades: supportsSub, supportsAutographNote: supportsAutograph };
        }
        setSupportResolutionLoading(false);
        badToast("Não foi possível validar o tipo do pedido para subgrades");
        return null;
    }

    const handleSubmit = async () => {
        if (supportResolutionLoading) {
            badToast("Aguarde a validação do tipo do pedido.");
            return null;
        }
        let supports = { supportsGeneralScore, supportsSubgrades, supportsAutographNote };
        if (productId !== resolvedProductId) {
            supports = await resolveSubgradesSupport(productId);
        }
        if (supports === null) {
            return null;
        }
        if (supports?.supportsAutographNote && !notaAutografo) {
            badToast("Selecione a nota do autógrafo.");
            return null;
        }
        const body = {
            gradableModelId,
            serialNumber,
            gradedAt: new Date(),
            ...(supports?.supportsGeneralScore ? { generalScore } : {}),
            ...(supports?.supportsSubgrades ? { surface, edges, centering, corners } : {}),
            ...(supports?.supportsAutographNote ? { notaAutografo } : {}),
            productId
        };
        const data = await createSerial(user.token, body);
        if(data.type === "success"){
            goodToast("Serial cadastrado");
            handleClose();
            refresh();
        }
        if(data.type === "error"){
            badToast(data.message);
        }
    }

    const doGenerateSerial = async () => {
        if(!gradableModelId){
            badToast("Insira um ID de modelo!")
            return null;
        }
        const gradableModelResponse = await getGradableModels(user.token, gradableModelId);
        if(gradableModelResponse.type === "error"){
            badToast(gradableModelResponse.message);
            return null;
        }
        const gradableModel = gradableModelResponse.data[0];
        if (!gradableModel) {
            badToast("Modelo avaliável não encontrado.");
            return null;
        }
        const data = await generateSerial(user.token, gradableModel.serialInitial || "XX");
        if(data.type === "error"){
            badToast(data.message);
            return null;
        }
        if(data.type === "success"){
            setSerialNumber(data.data);
        }
    }

    return (
        <Modal handleClose={handleClose}>
            <h2 className="my-6 text-orange-500">NOVO SERIAL</h2>
            <div className="grid grid-cols-2 gap-2 w-fit m-auto">
                <label>ID do Modelo Avaliável: </label>
                <input type="text" value={gradableModelId} onChange={e => setGradableModelId(e.target.value)} className="text-center text-black"/>
                <label>ID do Pedido: </label>
                <input type="text" value={productId} onChange={e => { const value = e.target.value; setProductId(value); setResolvedProductId(""); if (value) { void resolveSubgradesSupport(value); } }} className="text-center text-black"/>
                <label>Número de Serial: </label>
                <div className="flex">
                    <label>{serialNumber}</label>
                    <Button classes="bg-green-400 hover:bg-green-600 w-1/3" click={doGenerateSerial}>Gerar</Button>
                </div>
                {
                    supportsGeneralScore ?
                        <>
                            <label>Nota Geral: </label>
                            <input type="text" value={generalScore} onChange={e => setGeneralScore(e.target.value)} className="text-center text-black"/>
                        </>
                        : null
                }
                {
                    supportsSubgrades ?
                        <>
                            <label>Surface: </label>
                            <input type="text" value={surface} onChange={e => setSurface(e.target.value)} className="text-center text-black"/>
                            <label>Edges: </label>
                            <input type="text" value={edges} onChange={e => setEdges(e.target.value)} className="text-center text-black"/>
                            <label>Centering: </label>
                            <input type="text" value={centering} onChange={e => setCentering(e.target.value)} className="text-center text-black"/>
                            <label>Corners: </label>
                            <input type="text" value={corners} onChange={e => setCorners(e.target.value)} className="text-center text-black"/>
                        </>
                        : null
                }
                {
                    supportsAutographNote ?
                        <>
                            <label>Nota do Autógrafo: </label>
                            <select value={notaAutografo} onChange={e => setNotaAutografo(e.target.value)} className="text-center text-black">
                                <option value="">Selecione</option>
                                <option value="Autentico">Autentico</option>
                                <option value="8">8</option>
                                <option value="9">9</option>
                                <option value="10">10</option>
                            </select>
                        </>
                        : null
                }
            </div>
            <div className="flex w-1/2 mx-auto my-4">
                <Button classes="bg-red-500 hover:bg-red-800 w-1/3" click={handleClose}>Cancelar</Button>
                <Button classes="bg-green-400 hover:bg-green-600 w-1/3" click={handleSubmit} disabled={supportResolutionLoading}>Cadastrar</Button>
            </div>
        </Modal>
    )
}

export default CreateSerialModal;
